import type { ApiError } from '@homecare/contracts';

/**
 * The one place the console talks to the API.
 *
 * Every failure arrives as the §26.2 envelope, so every failure the UI renders
 * has a `userMessage` written for the person reading it. Nothing in this app
 * ever composes an error string of its own: a status code on screen is a
 * developer telling a caregiver to go and read the HTTP spec.
 */
export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly envelope: ApiError,
  ) {
    super(envelope.message);
    this.name = 'ApiRequestError';
  }

  get code(): ApiError['code'] {
    return this.envelope.code;
  }

  /** Rendered to the person, verbatim. */
  get userMessage(): string {
    return this.envelope.userMessage;
  }

  /** Field-level messages, for inline validation on a form. */
  get details(): Record<string, string[]> {
    return this.envelope.details ?? {};
  }
}

/**
 * The envelope we fall back to when the network never reached the API.
 *
 * A fetch that fails has no envelope, but the person still needs a sentence and
 * a next step. Written here rather than at each call site so an offline
 * caregiver gets the same words everywhere.
 */
const OFFLINE: ApiError = {
  code: 'INTERNAL_ERROR',
  message: 'Network request failed before reaching the API.',
  userMessage: 'We could not reach the server. Check your connection and try again.',
  requestId: 'offline',
  docsUrl: 'https://docs.homecare.imotechsolutions.com/errors/INTERNAL_ERROR',
};

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  accessToken?: string | null;
  signal?: AbortSignal;
}

const BASE = import.meta.env['VITE_API_URL'] ?? 'http://localhost:3000';

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  let response: Response;

  try {
    response = await fetch(`${BASE}${path}`, {
      method: options.method ?? 'GET',
      headers: {
        ...(options.body === undefined ? {} : { 'content-type': 'application/json' }),
        ...(options.accessToken ? { authorization: `Bearer ${options.accessToken}` } : {}),
      },
      ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch {
    throw new ApiRequestError(0, OFFLINE);
  }

  const text = await response.text();
  const parsed: unknown = text.length > 0 ? JSON.parse(text) : null;

  if (!response.ok) {
    // If the body is not an envelope, something upstream of the API answered —
    // a proxy, a load balancer. The person still gets a sentence.
    const envelope =
      parsed && typeof parsed === 'object' && 'userMessage' in parsed
        ? (parsed as ApiError)
        : OFFLINE;

    throw new ApiRequestError(response.status, envelope);
  }

  return parsed as T;
}

/**
 * A multipart upload — one file, plus text fields beside it.
 *
 * `FormData` rather than the JSON path above, because the alternative is base64
 * in a JSON body: it inflates a roster by a third and makes the browser hold
 * the whole file in memory twice. The `content-type` header is deliberately not
 * set — the browser has to add the multipart boundary itself, and setting it by
 * hand produces a request the server cannot parse.
 */
export async function requestMultipart<T>(
  path: string,
  file: File,
  fields: Record<string, string>,
  accessToken?: string | null,
): Promise<T> {
  const form = new FormData();
  form.append('file', file, file.name);
  for (const [name, value] of Object.entries(fields)) form.append(name, value);

  let response: Response;

  try {
    response = await fetch(`${BASE}${path}`, {
      method: 'POST',
      headers: accessToken ? { authorization: `Bearer ${accessToken}` } : {},
      body: form,
    });
  } catch {
    throw new ApiRequestError(0, OFFLINE);
  }

  const text = await response.text();
  const parsed: unknown = text.length > 0 ? JSON.parse(text) : null;

  if (!response.ok) {
    const envelope =
      parsed && typeof parsed === 'object' && 'userMessage' in parsed
        ? (parsed as ApiError)
        : OFFLINE;

    throw new ApiRequestError(response.status, envelope);
  }

  return parsed as T;
}
